import { FC, useCallback, useMemo, useState, type JSX } from 'react';

import useGetExistingCustomerContactEmails from '@/api/queries/customers/useGetExistingCustomerContactEmails';
import { Button, Modal } from '@payaca/ambrosia';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import ValidationFeedbackBlock from '@payaca/components/validationFeedbackBlock/ValidationFeedbackBlock';
import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import { getCustomerContactEmailFieldValidator } from '@payaca/helpers/customerValidationHelper';
import { substituteAndSplitEmailTemplate } from '@payaca/helpers/emailSubstitutionHelper';
import { getIsRequiredFieldValidator } from '@payaca/helpers/fieldValidationHelper';
import * as customerActions from '@payaca/store/customer/customerActions';
import { useCustomer } from '@payaca/store/hooks/appState';
import { CustomerContact } from '@payaca/types/customerTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { get } from 'lodash-es';
import { useDispatch } from 'react-redux';
import LoqateAdvisoryEmailInputField from '../loqateAdvisoryEmailInputField/LoqateAdvisoryEmailInputField';
import SendableDocumentEmailControl from '../previewAndSendSendableDocument/SendableDocumentEmailControl';
import './BounceResendModal.sass';

const emailCopyValidator = (
  fieldName: string,
  formState: { [key: string]: any }
) => {
  const value = get(formState, fieldName);

  if (!value.preButton?.trim().length && !value.postButton?.trim().length) {
    return {
      isValid: false,
      errors: ['The email must have some content'],
    };
  }

  return {
    isValid: true,
  };
};

type Props = {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  customerId: number;
  customerContactId?: number;
  resendConfig: {
    handleResend: (requestData: {
      emailCopy: {
        preButton: string;
        postButton: string;
      };
    }) => Promise<void>;
    prompt?: string;
    documentType: 'invoice' | 'quote' | 'estimate' | 'change proposal';
    emailConfig: {
      htmlTemplate?: string;
      subject: string;
      bodyTemplate: string;
      substitutions?: { find: string; replaceWith: string }[];
    };
  };
};
const BounceResendModal: FC<Props> = ({
  title,
  isOpen,
  onClose,
  customerId,
  customerContactId,
  resendConfig,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const [isProcessing, setIsProcessing] = useState(false);
  const [showValidationFeedback, setShowValidationFeedback] = useState(false);
  const customer = useCustomer(customerId);

  const customerContact = useMemo(
    () =>
      customer
        ? getJobContactFromCustomer(customer, customerContactId || null)
        : undefined,
    [customer, customerContactId]
  );

  const { data: existingCustomerContactEmails, isLoading } =
    useGetExistingCustomerContactEmails();

  const fieldValidators = useMemo(() => {
    const disallowedEmails =
      existingCustomerContactEmails?.filter(
        (email) => email != customerContact?.emailAddress
      ) || [];

    const customerContactEmailFieldValidator =
      getCustomerContactEmailFieldValidator({
        existingCustomerContactEmails: disallowedEmails,
      });

    return {
      emailAddress: [
        getIsRequiredFieldValidator({ readableName: 'Customer email' }),
        customerContactEmailFieldValidator,
      ],
      emailCopy: [emailCopyValidator],
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'customerContact.emailAddress'
  }, [existingCustomerContactEmails]);

  const initialFormState = useMemo(() => {
    const splitTemplate = substituteAndSplitEmailTemplate(
      resendConfig.emailConfig.bodyTemplate,
      resendConfig.emailConfig.substitutions || []
    );

    return {
      htmlTemplate: resendConfig.emailConfig.htmlTemplate,
      emailAddress: customerContact?.emailAddress,
      emailCopy: splitTemplate,
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: customer
  }, [customer, customerContact, resendConfig.emailConfig]);

  const onSubmit = useCallback(
    (formState: { [key: string]: any }) => {
      if (!customer) return;
      setIsProcessing(true);
      const contacts = customer.contacts.map(
        (customerContact: CustomerContact, index: number) => {
          if (customerContact.id === customerContact?.id) {
            // update email on job contact
            return {
              ...customerContact,
              emailAddress: formState.emailAddress,
            };
          } else {
            return customerContact;
          }
        }
      );

      const customerToPersist = {
        ...customer,
        contacts: contacts,
      };

      dispatch(
        customerActions.requestPersistCustomer(customerToPersist, () => {
          void resendConfig
            .handleResend({
              emailCopy: formState.emailCopy,
            })
            .then(() => {
              setIsProcessing(false);
              onClose();
            });
        })
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', 'resendConfig', and 'onClose'
    [customer, customerContact]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <div className="form-content">
          <ValidatedFieldWrapper
            validationResult={{
              isValid: validationState.emailAddress?.isValid,
            }}
            isTouched={touchedState['emailAddress'] || false}
          >
            <LoqateAdvisoryEmailInputField
              label={'Update customer email address'}
              value={formState.emailAddress}
              name="emailAddress"
              onChange={onFieldChange}
              onTouch={onFieldTouch}
              disableValidation={
                validationState['emailAddress']
                  ? !validationState['emailAddress'].isValid
                  : false
              }
              styleVariant={InputStyleVariant.OUTSIZE}
            />
          </ValidatedFieldWrapper>
          {customerContact && (
            <SendableDocumentEmailControl
              buttonText={`View ${resendConfig.documentType}`}
              emailSubject={resendConfig.emailConfig.subject}
              emailState={{
                htmlTemplate: formState.htmlTemplate,
                emailCopy: formState.emailCopy,
              }}
              onChange={(value) => value && onFieldChange(value)}
            />
          )}

          <div className="actions-container">
            {showValidationFeedback && (
              <ValidationFeedbackBlock
                validationResults={Object.values(validationState)}
                isDismissable={true}
                onDismissed={() => setShowValidationFeedback(false)}
              />
            )}
            <Button
              onClick={() =>
                isValid
                  ? !isProcessing && onSubmit(formState)
                  : setShowValidationFeedback(true)
              }
              isProcessing={isProcessing}
              replaceContentWhenProcessing
            >
              {resendConfig.prompt || 'Update and resend'}
            </Button>
          </div>
        </div>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'resendConfig.documentType', 'resendConfig.emailConfig.subject', 'customerContact', and 'resendConfig.prompt'
    [isProcessing, onSubmit, showValidationFeedback]
  );

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      onClose={onClose}
      className="bounce-resend-modal"
    >
      <Modal.Body>
        <ValidatedForm
          fieldValidators={fieldValidators}
          initialFormState={initialFormState}
          renderFormContents={renderFormContents}
        />
      </Modal.Body>
    </Modal>
  );
};

export default BounceResendModal;
